<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-4 overflow-auto pr-2">
    <div class="print-hide bg-white rounded-xl shadow-sm border border-slate-200 overflow-visible border-t-4 border-t-[#0d4169]">
      <div class="px-6 pt-5 pb-3">
        <h2 class="text-base font-bold text-[#0d4169]">Store Request Report</h2>
      </div>
      <form @submit.prevent="generateReport" class="px-6 pb-4 pt-2">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Start Date <span v-if="isStartDateRequired" class="text-red-500">*</span></label>
            <input
              v-model="filters.startDate"
              type="date"
              :required="isStartDateRequired"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">End Date</label>
            <input
              v-model="filters.endDate"
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Category</label>
            <select
              v-model="filters.categoryId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option :value="null">All</option>
              <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Department</label>
            <select
              v-model="filters.departmentId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option :value="null">All</option>
              <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Item</label>
            <input
              v-model="filters.itemQuery"
              type="text"
              placeholder="Search item/product..."
              list="approval-request-report-item-suggestions"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
            <datalist id="approval-request-report-item-suggestions">
              <option v-for="name in itemSuggestions" :key="name" :value="name" />
            </datalist>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Req. number</label>
            <input
              v-model="filters.reqNo"
              type="text"
              placeholder="e.g. REQ-20260001"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Is Ingredient</label>
            <select
              v-model="filters.ingredientFilter"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="all">All</option>
              <option value="1">Ingredient</option>
              <option value="0">Material</option>
            </select>
          </div>
        </div>

        <div class="mt-3 pt-3 pr-8 border-t border-slate-100 flex justify-between items-center gap-3">
          <button
            type="button"
            @click="handlePrintReport"
            class="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md shadow-md hover:bg-slate-300 transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="loading"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9V2h12v7"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            <span>Print</span>
          </button>
          <button type="submit" class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95">
            <div v-if="loading" class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></div>
            <span v-else>Generate </span> 
          </button>
        </div>
      </form>
    </div>

    <div class="w-full overflow-x-auto shadow-sm rounded-xl pa-1 print-container approval-table-wrap">
      <div class="hidden print:block mb-6">
        <div class="print-doc-header print-doc-header-grid">
          <div class="print-doc-left">
            <div class="print-company-block">
              <img
                v-if="printCompanyLogo"
                :src="printCompanyLogo"
                :alt="printCompanyName || 'Company Logo'"
                class="print-company-logo"
              />
              <div v-else class="print-company-logo-fallback">Logo</div>
              <div class="print-company-text">
                <div class="print-company-name">{{ printCompanyName }}</div>
              </div>
            </div>
          </div>
          <h1 class="print-doc-title text-xl font-bold text-center mb-2">Store Request Report</h1>
          <div class="print-doc-meta print-doc-right">
            <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
          </div>
        </div>
        <hr class="my-4 border-black" />
      </div>
      <table class="w-full border-collapse bg-white overflow-hidden print-table">
        <thead>
          <tr>
            <th class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider">No.</th>
            <th @click="sortBy('reqDate')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Req. Date <span v-if="sortKey === 'reqDate'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('apprDate')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Appr. Date <span v-if="sortKey === 'apprDate'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('reqDepartment')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Req.Department <span v-if="sortKey === 'reqDepartment'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('reqName')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Req. name <span v-if="sortKey === 'reqName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('reqNumber')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Req. number <span v-if="sortKey === 'reqNumber'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('category')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Category <span v-if="sortKey === 'category'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('item')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Item <span v-if="sortKey === 'item'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('uom')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              U.O.M <span v-if="sortKey === 'uom'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('reqQty')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Req. Qty <span v-if="sortKey === 'reqQty'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('apprQty')" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider cursor-pointer">
              Appr. Qty <span v-if="sortKey === 'apprQty'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in sortedRows" :key="row.key" class="bg-white border-b">
            <td class="px-2 py-1 text-slate-700 text-center">{{ i + 1 }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.reqDate }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.apprDate }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.reqDepartment }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.reqName }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.reqNumber }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.category }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.item }}</td>
            <td class="px-2 py-1 text-slate-700 text-center">{{ row.uom }}</td>
            <td class="px-2 py-1 text-[#0d4169] italic font-semibold text-center">{{ row.reqQty }}</td>
            <td class="px-2 py-1 text-[#0d4169] italic font-semibold text-center">{{ row.apprQty }}</td>
          </tr>
          <tr v-if="sortedRows.length === 0">
            <td colspan="10" class="px-4 py-10 text-center text-slate-400 italic text-sm">No data found for the selected filters.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import store from '../../../store'
import { apiFetch } from '../../../utils/api'

const departments = ref([])
const categories = ref([])
const itemNames = ref([])
const report = ref([])
const loading = ref(false)
const sortKey = ref('reqDate')
const sortOrder = ref('asc')

const filters = ref({
  startDate: "",
  endDate: '',
  categoryId: null,
  departmentId: null,
  itemQuery: '',
  reqNo: '',
  ingredientFilter: 'all'
})

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)
const isStartDateRequired = computed(() =>
  String(filters.value.itemQuery || '').trim() === '' &&
  String(filters.value.reqNo || '').trim() === ''
)

const itemSuggestions = computed(() => {
  return [...new Set([...(itemNames.value || [])])]
})

async function loadLookups() {
  if (!companyId.value && !branchId.value) return
  const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
  try {
    const [depRes, catRes, itemRes] = await Promise.all([
      apiFetch(`/departments?${query}`),
      apiFetch(`/categories?${query}`),
      apiFetch(`/items?${query}`)
    ])
    if (depRes.ok) departments.value = await depRes.json().catch(() => [])
       departments.value.sort((a, b) => a.name.localeCompare(b.name))
    if (catRes.ok) categories.value = await catRes.json().catch(() => [])
       categories.value.sort((a, b) => a.name.localeCompare(b.name))
    if (itemRes.ok) {
      const items = await itemRes.json().catch(() => [])
      itemNames.value = Array.isArray(items) ? items.map(i => i?.itemName).filter(Boolean) : []
    }
  } catch (err) {
    console.error('Failed to load Approval Request report lookups', err)
  }
}

async function generateReport() {
  const hasLikeSearch = !isStartDateRequired.value
  if (!hasLikeSearch && !filters.value.startDate) {
    alert('Start date is required unless Item or Req. number is filled')
    return
  }
  loading.value = true
  const queryParts = []
  if (branchId.value) queryParts.push(`branchId=${branchId.value}`)
  else if (companyId.value) queryParts.push(`companyId=${companyId.value}`)
  if (!hasLikeSearch && filters.value.startDate) queryParts.push(`startDate=${filters.value.startDate}`)
  if (!hasLikeSearch && filters.value.endDate) queryParts.push(`endDate=${filters.value.endDate}`)
  if (filters.value.categoryId) queryParts.push(`categoryId=${filters.value.categoryId}`)
  if (filters.value.departmentId) queryParts.push(`departmentId=${filters.value.departmentId}`)
  if (filters.value.itemQuery) queryParts.push(`itemQuery=${encodeURIComponent(filters.value.itemQuery)}`)
  if (filters.value.reqNo) queryParts.push(`reqNo=${encodeURIComponent(filters.value.reqNo)}`)
  if (filters.value.ingredientFilter === '1') queryParts.push('is_ingredient=1')
  else if (filters.value.ingredientFilter === '0') queryParts.push('is_ingredient=0')

  try {
    const q = `?${queryParts.join('&')}`
    const res = await apiFetch(`/store-request/report/approval-request${q}`)
    if (!res.ok) {
      report.value = []
      return
    }
    const payload = await res.json().catch(() => ({}))
    report.value = Array.isArray(payload?.data) ? payload.data : []
  } catch (err) {
    console.error('Failed to fetch Approval Request report', err)
    report.value = []
  } finally {
    loading.value = false
  }
}

const rows = computed(() => {
  return (report.value || []).map((r, idx) => ({
    key: r?.id != null ? String(r.id) : `row-${idx}`,
    reqDate: r?.reqDate ?? '-',
    apprDate: r?.apprDate ?? r?.approvedDate ?? r?.approvedAt ?? '-',
    reqDepartment: r?.reqDepartment ?? '-',
    reqName: r?.reqName ?? '-',
    reqNumber: r?.reqNumber ?? '-',
    category: r?.category ?? '-',
    item: r?.item ?? '-',
    uom: r?.uom ?? '-',
    reqQty: r?.reqQty ?? 0,
    apprQty: r?.apprQty ?? 0
  }))
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

const sortedRows = computed(() => {
  const list = [...rows.value]
  const modifier = sortOrder.value === 'asc' ? 1 : -1

  const getVal = (row) => row[sortKey.value]

  list.sort((a, b) => {
    const va = getVal(a)
    const vb = getVal(b)
    const na = typeof va === 'number' ? va : (va !== null && va !== undefined && va !== '' ? Number(va) : NaN)
    const nb = typeof vb === 'number' ? vb : (vb !== null && vb !== undefined && vb !== '' ? Number(vb) : NaN)
    if (!Number.isNaN(na) && !Number.isNaN(nb)) {
      return (na - nb) * modifier
    }
    const sa = (va ?? '').toString().toLowerCase()
    const sb = (vb ?? '').toString().toLowerCase()
    if (sa < sb) return -1 * modifier
    if (sa > sb) return 1 * modifier
    return 0
  })

  return list
})

function handlePrintReport() {
  if (!sortedRows.value.length) {
    alert('No data to print. Please generate the report first.')
    return
  }
  window.print()
}

onMounted(async () => {
  await loadLookups()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 11px;
  line-height: 1.15;
  color: #1e293b;
}
th, td {
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  padding: 3px 4px;
  font-size: inherit;
}
th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

.approval-table-wrap {
  min-height: 70vh;
}

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  .overflow-x-auto {
    overflow: visible !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    table-layout: auto !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 4px !important;
    text-align: left !important;
    color: #000 !important;
    font-size: 10px !important;
    white-space: normal !important;
    word-break: break-word !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}
</style>

